"use client";

import { FC, useState, useEffect } from "react";
import style from "./style.module.scss";
import { ITafData, ITafForecast } from "@/interfaces/Map";
import MetarDetailsItem from "./MetarDetailsItem";
import NoDataSection from "./NoDataSection";
import {
  AltitudeUnitConverter,
  AltitudeUnit,
} from "@/helpers/unit-conversions";
import { TafDetailsItems, UI_TEXT, CONFIG } from "@/constants";
import { Nullable } from "@/types";

type Props = {
  taf: Nullable<ITafData[]>;
};

const TafDetails: FC<Props> = ({ taf }) => {
  const tafData = taf?.[0] || null;
  const forecasts: ITafForecast[] = (tafData?.forecasts ||
    []) as ITafForecast[];
  const [activeForecastIndex, setActiveForecastIndex] = useState<number>(0);

  useEffect(() => {
    if (forecasts.length > 0) {
      setActiveForecastIndex(0);
    }
  }, [tafData]);

  const handleTabClick = (index: number) => {
    if (forecasts && forecasts[index]) {
      setActiveForecastIndex(index);
    }
  };

  const activeForecast = forecasts[activeForecastIndex];

  const formatElevation = (
    elevation: Nullable<number>,
    unit: Nullable<string>
  ): string => {
    if (elevation === null || !unit) return "";
    const elevationInFeet = elevation;
    const backendUnit = unit;
    const elevationInMeters = AltitudeUnitConverter.fromUnit(
      elevationInFeet,
      backendUnit,
      AltitudeUnit.meters
    );
    const metersRounded = Math.round(elevationInMeters);
    const feetRounded = Math.round(elevationInFeet);
    return `${metersRounded} meters / ${feetRounded} ft`;
  };

  return (
    <div className={style.metarDetails}>
      {tafData && forecasts.length > 0 ? (
        <>
          <div className={style.detailsOptionTabs}>
            {forecasts.map((_, index) => (
              <button
                key={index}
                className={`${style.detailsOptionTabButton} ${
                  activeForecastIndex === index
                    ? style.detailsOptionTabButtonActive
                    : ""
                }`}
                onClick={() => handleTabClick(index)}
              >
                {UI_TEXT.TABS.TAF} {index + 1}
              </button>
            ))}
          </div>

          {activeForecast && (
            <>
              <div className={style.metarDetailsHeader}>
                <div className={style.metarDetailsTitleContainer}>
                  <h3 className={style.metarDetailsTitle}>
                    {UI_TEXT.TABS.TAF}:{" "}
                    {tafData?.rawTaf.split(CONFIG.RAW_DATA.SPLIT_DELIMITER)[
                      CONFIG.RAW_DATA.ICAO_CODE_INDEX
                    ] ?? ""}
                  </h3>
                </div>
                <div className={style.metarDetailsDescriptionContainer}>
                  <span className={style.metarDetailsDescription}>
                    {UI_TEXT.LABELS.RAW_TAF}
                  </span>
                  <span className={style.metarDetailsDescription}>
                    {tafData?.rawTaf}
                  </span>
                </div>
              </div>
              <div className={style.metarDetailsContent}>
                {TafDetailsItems.map((item, index) => (
                  <MetarDetailsItem
                    key={index}
                    iconSrc={item.iconSrc}
                    label={item.label}
                    value={item.getValue(tafData, activeForecast)}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <NoDataSection />
      )}
    </div>
  );
};

export default TafDetails;
